import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { GridModule, GroupService, GroupSettingsModel } from '@syncfusion/ej2-angular-grids';
import { DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { MultiSelectFilterComponent } from 'src/app/base/shared/multi-select-filter/multi-select-filter.component';
import { DeviceInteractionService } from 'src/app/domain/device-interaction/device-interaction.service';
import { IAbstractWidgetComponent, ParameterChangedData, WidgetMode } from '../../shared/abstract-widget.component';
import { interval, Subject, Subscription } from 'rxjs';
import { GridLocalDataBaseComponent } from 'src/app/base/shared/grid-base/grid-local-data-base.component';
import { MultiSelectFilterType } from 'src/app/base/shared/multi-select-filter/multi-select-filter-type';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { ExecuteZoneDeviceCommandDTO, ZoneClient, ZoneDeviceDTO, ZoneDeviceState, ZoneDeviceType } from 'src/app/base/models/models.swagger.generated';
import { DialogComponent as SyncfusionDialogComponent } from '@syncfusion/ej2-angular-popups';
import { AuthService } from 'src/app/base/services/auth.service';
import { UserDeviceControlWidgetSettingsFormType } from './user-device-control-widget-form-type';

@Component({
  selector: 'app-user-device-control-widget',
  imports: [TranslateModule, DialogModule, ReactiveFormsModule, TooltipModule, GridModule, DropDownListModule],
  templateUrl: './user-device-control-widget.component.html',
  styleUrl: './user-device-control-widget.component.scss',
  providers: [DeviceInteractionService, GroupService]
})
export class UserDeviceControlWidgetComponent extends GridLocalDataBaseComponent implements OnInit, OnDestroy, IAbstractWidgetComponent{
  @ViewChild('widgetSettingsDialog')
  public widgetSettingsDialog: SyncfusionDialogComponent;

  @ViewChild('zoneMultiselect')
  public zoneMultiselect: MultiSelectFilterComponent;

  public defaultSizeX = 3;
  public defaultSizeY = 2;
  public defaultPageSize = 5;

  WidgetMode = WidgetMode;

  public panelId: string;
  public groupOptions?: GroupSettingsModel;

  public editSettingsForm: FormGroup<UserDeviceControlWidgetSettingsFormType>;

  public MultiSelectFilterType = MultiSelectFilterType;
  public typeFilter = GridConstantsFactory.createCheckBoxFilter();
  public stateFilter = GridConstantsFactory.createCheckBoxFilter();

  public ZONE_NAME_COLUMN_NAME = 'zone.name';

  public get title(): string {
    const title = this.editSettingsForm.controls.title.value;
    if (title && title.length > 0) {
      return title;
    }
    return "i18n.domain.home.dashboard.shared.widget-types.UserDeviceControlWidget";
  }

  public get hasUserClientAssigned(): boolean {
    return this.authService.hasUserClientAssigned()
  }

  public get selectedZoneIds(): number[] {
    return this.editSettingsForm.controls.selectedZoneIds.value;
  }

  public set selectedZoneIds(zoneIds: number[]) {
    this.editSettingsForm.controls.selectedZoneIds.setValue(zoneIds);
    this.deviceInteractionService.zoneIds = zoneIds;
  }

  private onDeleteCallback: (panelId: string) => void;
  private autoRefreshSubscription: Subscription;

  constructor(translateService: TranslateService,
              private deviceInteractionService: DeviceInteractionService,
              private fb: FormBuilder,
              private authService: AuthService,
              private zoneClient: ZoneClient
  ) {
    super(null, translateService, null);

    this.editSettingsForm = this.fb.group<UserDeviceControlWidgetSettingsFormType>({
      saveGridFilters: this.fb.control<boolean>(true),
      gridFilter: this.fb.control<string>(null),
      title: this.fb.control<string>(null),
      selectedZoneIds: this.fb.control<number[]>(null, Validators.required)
    });
  }
  public mode: WidgetMode = WidgetMode.View;
  public parametersChanged: Subject<ParameterChangedData> = new Subject<ParameterChangedData>();
  public isPersonalMode = false;

  public ngOnInit(): void {
    this.groupOptions = { showDropArea: false, columns: [this.ZONE_NAME_COLUMN_NAME] };

    this.getData();

    // Auto-refresh the grid every minute (60000 ms)
    this.autoRefreshSubscription = interval(60000).subscribe(() => {
      this.getData();
    });
  }

  public ngOnDestroy(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
    }
  }

  public initializeParameters(panelId: string, serializedParameters: string, onDeleteCallback: (panelId: string) => void) {
    this.panelId = panelId;
    this.onDeleteCallback = onDeleteCallback;
  }

  public onDelete() {
    this.onDeleteCallback(this.panelId);
  }

  public onConfigure() {
    this.widgetSettingsDialog.show();
  }

  public onForceRefresh() {
    this.getData();
    this.grid.refresh();
  }

  public onCloseSettingsDialog() {
    this.widgetSettingsDialog.hide();
  }

  public saveParameters() {
    this.widgetSettingsDialog.hide();
    this.parametersChanged.next({
      panelId: this.panelId,
      serializedParamerters: JSON.stringify(this.editSettingsForm.getRawValue())
    });
  }

  public executeDeviceCommand(deviceId: number, command: any, position: string): void {
    this.deviceInteractionService.executeDeviceCommand(new ExecuteZoneDeviceCommandDTO({ command, deviceId }))
      .subscribe(() => {
        this.getData();
        this.deviceInteractionService.observeCommandStatus(deviceId, position).subscribe(() => {
          this.getData();
        });
      });
  }

  public getDeviceType(type: ZoneDeviceType): string {
    return 'i18n.shared.zone-device-enums.device-type.' + ZoneDeviceType[type];
  }

  public formatType(device: ZoneDeviceDTO): string {
    if (!device || !device.deviceType) {
      return '';
    }
    return this.translateService.instant('i18n.shared.zone-device-enums.device-type.' + ZoneDeviceType[device.deviceType]);
  }

  public getDeviceState(status: ZoneDeviceState): string {
    return 'i18n.shared.zone-device-enums.state.' + ZoneDeviceState[status];
  }

  public formatState(device: ZoneDeviceDTO): string {
    if (!device || !device.state) {
      return '';
    }
    return this.translateService.instant('i18n.shared.zone-device-enums.state.' + ZoneDeviceState[device.state]);
  }

  protected override initializeEditForm(data: any): void {
    throw new Error('Method not implemented.');
  }
  protected override getEditDialogTitleParams(rowData: any): object {
    throw new Error('Method not implemented.');
  }

  private getData(): void {
    this.deviceInteractionService.getAllCommandableDevicesForCurrentUser().subscribe((devices: ZoneDeviceDTO[]) => {
      let selectedZones = this.editSettingsForm.controls.selectedZoneIds.value;
      if (selectedZones && selectedZones.length > 0) {
        this.data = devices.filter(device => selectedZones.includes(device.zone?.id));
      } else {
        this.data = devices;
      }
    });
  }

}
